/**
 * SOLE Care — warranty UI controller + attach adapter (3 A/B-test variants).
 *
 * Variants (set per product via the `warranty_ui_mode` section setting / template):
 *   A 'dropdown'  — inline plan selector in the buy box; selected plan attaches after the machine ATC.
 *   B 'popup'     — after the machine ATC, a <dialog> offers the plan; accept attaches it.
 *   C 'cart_only' — no PDP surface; a card in the cart drawer + cart page adds the plan.
 *
 * Attach adapter (the swappable seam — see PROSURO-REVERSE-ENGINEERING.md):
 *   'standalone' (preview/test) — adds a real test product (guardsman-protection-plan) as its own cart
 *      line by submitting a detached /cart/add form, REUSING sole-cart-ajax.js (so the drawer/cart morph
 *      and the cart:update event are handled by the existing pipeline — no duplicated render logic).
 *   'prosuro' (production) — drives the live Prosuro app via its documented `protectionAdded` /
 *      `protectionDeclined` events so Prosuro keeps owning pricing + Guardsman registration.
 *
 * Progressive enhancement: with JS off the machine still adds normally; warranty is an enhancement.
 */

import { CartUpdateEvent } from '@theme/events';

// PDP config wins when present (earlier in the DOM); the cart page falls back to the card's own config.
const cfgEl = document.querySelector('[data-sole-warranty-config], [data-sole-warranty-cart-config]');
/** @type {any} */
let config = null;
try { config = cfgEl ? JSON.parse(cfgEl.textContent || 'null') : null; } catch (_) { config = null; }

/** plan pending attach after the machine add resolves (variant A). */
let pendingWarranty = null;
/** a machine add is in flight that should trigger the popup (variant B). */
let awaitingMachineForPopup = false;

/* ----------------------------------------------------------------------- helpers */

function trackGA(event, plan) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      warranty_variant: config?.uiMode,
      warranty_attach: config?.attachMode,
      warranty_plan: plan?.id || null,
      warranty_price: plan ? Number(plan.price) : null,
      product_title: config?.product?.title || null,
    });
  } catch (_) {}
}

/** Read the checked plan from a fieldset; null if "decline"/none. */
function getSelectedPlan(fieldset) {
  const input = fieldset?.querySelector('input[data-plan-id]:checked');
  if (!input || input.getAttribute('data-plan-id') === 'decline') return null;
  return {
    id: input.getAttribute('data-plan-id'),
    term: input.getAttribute('data-plan-term'),
    sku: input.getAttribute('data-plan-sku'),
    price: input.getAttribute('data-plan-price'),
    label: input.getAttribute('data-plan-label'),
  };
}

function planPropValue(plan) {
  return `$${plan.price} (${plan.term} Year${plan.term === '1' ? '' : 's'})`;
}

/* ----------------------------------------------------------------------- prosuro form sync (variant A)

   The live Prosuro app prices + registers a plan when the MACHINE's /cart/add carries its 4 line-item
   properties (see PROSURO-REVERSE-ENGINEERING.md §2 + qa/QA-2026-06-21-prosuro-pricing-spike.md). On the
   rebuild, Prosuro's `protection-product-offer-mini` block renders as a below-fold band OUTSIDE the
   pdp7-form, so its hidden inputs do NOT auto-ride the machine ATC. For variant A in `prosuro` mode we
   therefore mirror those 4 properties into hidden inputs we keep INSIDE the buy-box form, synced whenever
   the shopper picks/declines a plan — so the native add + sole-cart-ajax serialization carry them with no
   separate line and no submit-ordering race.

   `_PlanOptions` (the per-product token with the category/terms ids we can't fabricate) is read straight
   from Prosuro's block input; `_SelectedPlan`/`Product`/`Protection` prefer Prosuro's own filled values
   (it fills them in response to our `protectionAdded` dispatch) and fall back to values we compute. */

/** Read the value of one of Prosuro's hidden attribute inputs, or '' if its block isn't present. */
function readProsuroInput(id) {
  const el = document.getElementById(id);
  return el && 'value' in el ? el.value || '' : '';
}

/* --- per-product token: single source of truth for price ---------------------------------------------
   Prosuro's `_PlanOptions` token is per-product (`vid:cat:terms~term-SKU-price,…`, e.g. F89
   `~1-HFTG12-244.99,2-HFTG24-279.99,3-HFTG36-349.99`). The fieldset is server-rendered from the FLAT
   `warranty_tiers` placeholder, so we must NOT trust its sku/price for the charge — we resolve every
   attach against the token instead, and rewrite the displayed price to match. This makes the SOLE-branded
   UI price per-product exactly like Prosuro's own widget, with zero per-product theme config. */

/** Parse the per-product token into `{ term: {sku, price} }`. Empty if no token / malformed. */
function parseTierMap(token) {
  const map = {};
  if (!token || token.indexOf('~') === -1) return map;
  token.slice(token.indexOf('~') + 1).split(',').forEach((t) => {
    const p = t.trim().split('-'); // term-SKU-price — parse from the ENDS: the SKU may contain a dash,
    if (p.length < 3) return;      // and price must be numeric, so we never charge a garbage `_SelectedPlan`.
    const term = p[0];
    const price = p[p.length - 1];
    const sku = p.slice(1, -1).join('-');
    if (term && sku && /^\d+(\.\d{1,2})?$/.test(price)) map[term] = { sku, price };
  });
  return map;
}

/**
 * Re-resolve a fieldset plan to Prosuro's REAL per-product tier (sku + price from the token), so we charge
 * Prosuro's own price — never the flat display placeholder (which only matches F65's HFTC band by accident).
 * @returns the plan with real sku/price/id, or null if this product's token doesn't offer that term.
 */
function resolveProsuroPlan(plan, token) {
  if (!plan) return plan;
  const tok = token != null ? token : readProsuroInput('prosuroAttribute-planOptions');
  const tier = parseTierMap(tok)[plan.term];
  if (!tier) return null;
  return { ...plan, sku: tier.sku, price: tier.price, id: `${plan.term}-${tier.sku}` };
}

/** Run `cb(token)` once Prosuro's per-product token input is populated (it loads async), or `cb('')` if not. */
function whenProsuroToken(cb, tries = 25) {
  const tick = () => {
    const token = readProsuroInput('prosuroAttribute-planOptions');
    if (token) { cb(token); return; }
    if (tries-- > 0) { setTimeout(tick, 250); return; }
    // Timed out: token never loaded. Distinct from a genuinely-uncovered SKU — surface it so the
    // silent-suppression rate (warranty hidden on a possibly-covered PDP) is observable, not invisible.
    console.warn('[sole-warranty] Prosuro token never loaded — warranty suppressed on a possibly-covered PDP');
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'warranty_token_timeout', product_title: config?.product?.title || null }); } catch (_) {}
    cb('');
  };
  tick();
}

/**
 * Rewrite each fieldset's DISPLAYED price/sku from the per-product token, so the UI never shows the flat
 * placeholder ($184.99) while the cart charges the real price ($349.99). Terms the product doesn't offer are
 * hidden; a product not in Prosuro's catalog hides its whole inline surface (mirrors live — no warranty on
 * unwired SKUs). PDP-only (dropdown/popup): the token lives in Prosuro's on-page block.
 */
function applyRealPricesToDom() {
  const map = parseTierMap(readProsuroInput('prosuroAttribute-planOptions'));
  document.querySelectorAll('[data-sole-warranty-fieldset]').forEach((fs) => {
    // Per-line cart offers already render their OWN correct per-product prices (from that line's token) — never
    // let a PDP token rewrite them.
    if (fs.closest('[data-sole-warranty-line]')) return;
    let offered = 0;
    fs.querySelectorAll('input[data-plan-id]').forEach((input) => {
      if (input.getAttribute('data-plan-id') === 'decline') return;
      const term = input.getAttribute('data-plan-term');
      const wrap = input.closest('.sole-warranty-tier, .sole-warranty-opt');
      const tier = map[term];
      if (!tier) { if (wrap) wrap.hidden = true; return; } // this product doesn't offer this term
      offered += 1;
      input.setAttribute('data-plan-sku', tier.sku);
      input.setAttribute('data-plan-price', tier.price);
      input.setAttribute('data-plan-id', `${term}-${tier.sku}`);
      input.value = `${term}-${tier.sku}`;
      const priceEl = wrap && wrap.querySelector('.sole-warranty-tier__price, .sole-warranty-opt__price');
      if (priceEl) {
        const small = priceEl.querySelector('small');
        priceEl.textContent = `$${tier.price}`;
        if (small) priceEl.appendChild(small); // preserve the " · one-time" suffix (card variant)
      }
    });
    // Reveal only where the product actually has coverage; otherwise keep hidden (uncovered SKU, mirrors live).
    const surface = fs.closest('[data-sole-warranty-inline], [data-sole-warranty-card]') || fs;
    if (offered > 0) { surface.hidden = false; surface.removeAttribute('data-sole-warranty-pending'); }
    else { surface.hidden = true; }
  });
}

/* --- per-product Terms & Conditions link ------------------------------------------------------------
   Prosuro's own dev (UTS/Amynta) confirmed the customer "See plan details" link must point at
   protect.prosuro.com/document?id=<terms>, where <terms> is the 3rd colon-field of the per-product
   token prefix (`vid:cat:terms~term-SKU-price,…`) — the SAME token we already read for pricing. The
   theme setting `warranty_terms_url` ships a placeholder that lands on Prosuro's ADMIN LOGIN; we
   override every details link here so shoppers reach the real Guardsman T&C PDF instead. */
const PROSURO_DOC_BASE = 'https://protect.prosuro.com/document?id=';

/** Build the T&C doc URL from a Prosuro `_PlanOptions` token, or '' if it carries no terms id. */
function prosuroTermsUrl(token) {
  if (!token || token.indexOf('~') === -1) return '';
  const terms = token.slice(0, token.indexOf('~')).split(':')[2];
  return terms ? PROSURO_DOC_BASE + encodeURIComponent(terms) : '';
}

/** Repoint every "See plan details" link at the real per-product T&C document. PDP links use the PDP
    token; each cart line uses its own `data-wty-token`. A link with no resolvable terms id is left on
    the Liquid fallback (never the admin-login placeholder). */
function applyTermsLinks(pdpToken) {
  const pdpUrl = prosuroTermsUrl(pdpToken != null ? pdpToken : readProsuroInput('prosuroAttribute-planOptions'));
  document.querySelectorAll('.sole-warranty-details-link, .sole-warranty-fieldset__terms').forEach((a) => {
    const line = a.closest('[data-sole-warranty-line]');
    const href = line ? prosuroTermsUrl(line.getAttribute('data-wty-token')) : pdpUrl;
    if (href) a.setAttribute('href', href);
  });
}

/** Ensure a hidden `properties[name]` input exists inside `form` (created once, reused), and return it. */
function ensureFormProp(form, name) {
  let el = form.querySelector(`input[data-sole-warranty-prop="${name}"]`);
  if (!el) {
    el = document.createElement('input');
    el.type = 'hidden';
    el.name = `properties[${name}]`;
    el.setAttribute('data-sole-warranty-prop', name);
    form.appendChild(el);
  }
  return el;
}

/** Remove the warranty property inputs we injected into `form` (decline / no selection). */
function clearFormProps(form) {
  form.querySelectorAll('input[data-sole-warranty-prop]').forEach((el) => el.remove());
}

/**
 * Variant A + prosuro: mirror the chosen plan's 4 Prosuro properties onto the machine buy-box form.
 * @param {{id:string,term:string,sku:string,price:string,label:string}|null} plan  null = declined/none
 */
function prosuroSyncForm(plan) {
  const form = document.querySelector('form.pdp7-form');
  if (!form) return;

  if (!plan) {
    // Decline / no selection → carry nothing. Without `_PlanOptions` the machine line is ineligible, so
    // Prosuro's own post-ATC auto-offer won't double-fire either.
    document.dispatchEvent(new CustomEvent('protectionDeclined'));
    clearFormProps(form);
    return;
  }

  const planOptions = readProsuroInput('prosuroAttribute-planOptions'); // per-product token, from the block
  const real = resolveProsuroPlan(plan, planOptions);
  if (!real) {
    // The product's Prosuro token doesn't offer this term (or hasn't loaded) → carry nothing rather than
    // ship a placeholder `_SelectedPlan` that won't reconcile against `_PlanOptions` at checkout.
    console.warn('[sole-warranty] selected term not in Prosuro token — cannot price; carrying no plan');
    document.dispatchEvent(new CustomEvent('protectionDeclined'));
    clearFormProps(form);
    return;
  }

  // Tell Prosuro a plan is selected (suppresses its post-ATC auto-offer); dispatch the REAL sku/price so
  // Prosuro's own hidden-input state matches the token, not the flat display placeholder.
  document.dispatchEvent(new CustomEvent('protectionAdded', {
    detail: { term: real.term, sku: real.sku, price: real.price },
  }));

  ensureFormProp(form, '_PlanOptions').value = planOptions;
  // Price = Prosuro's per-product token (single source of truth), NOT the display placeholder.
  ensureFormProp(form, '_SelectedPlan').value = `${real.id}-${real.price}`; // term-SKU-price, real
  // Prefer Prosuro's own product-price input; fall back to the buy-box price so `Product` (a display prop)
  // is never blank if Prosuro hasn't filled it (parity with the post-ATC promote path).
  const product = readProsuroInput('prosuroAttribute-productPrice')
    || (document.querySelector('[data-pdp7-price]')?.textContent || '').trim();
  if (product) ensureFormProp(form, 'Product').value = product;
  ensureFormProp(form, 'Protection').value = planPropValue(real);
}

/* ------------------------------------------------------- prosuro post-ATC attach (variants B + C)

   For B (popup) and C (cart card) the plan is accepted AFTER the bare machine is already in the cart, so
   we can't bundle properties into the add. Instead we mirror Prosuro's OWN post-ATC mechanism
   (`handleCartProtectionAdd` in prosuro-js-core-runtime.js): promote an existing line via /cart/change.js.

   To keep Prosuro's auto-offer popup dormant (it would compete with our UI) the machine adds carrying the
   per-product token under a DIFFERENT key — `_StagedPlanOptions` — so `_PlanOptions` is absent until we
   accept (auto-offer eligibility requires `_PlanOptions` present + `_SelectedPlan` empty). On accept we
   GET /cart.js (fresh — a property change RE-KEYS the line), merge the line's existing properties, promote
   `_StagedPlanOptions → _PlanOptions` and fill `_SelectedPlan/Product/Protection` in Prosuro's exact
   formats, then POST /cart/change.js and refresh through the theme's own morph (CartUpdateEvent + sections).
   Prosuro's fetch/XHR monkey-patch fires on every /cart/change, so we guard with
   `__prosuroSkipCartMonkeyPatch` (its own flag) to stop it racing our morph with its DOM injection. */

/** Inject (and keep populated) the hidden `_StagedPlanOptions` token on the buy-box form. */
function prosuroStageToken() {
  const form = document.querySelector('form.pdp7-form');
  if (!form) return;
  const input = ensureFormProp(form, '_StagedPlanOptions');
  // Prosuro's block populates its token shortly after load — poll until it's available, then it stays
  // (the token is static per product, so a single successful read is enough for every subsequent add).
  let tries = 0;
  const tick = () => {
    const token = readProsuroInput('prosuroAttribute-planOptions');
    if (token) { input.value = token; return; }
    if (tries++ < 25) setTimeout(tick, 250);
  };
  tick();
}

/** Gather the cart section ids to re-render (mirrors sole-cart-ajax.js so the same morph pipeline runs). */
function cartSectionIds() {
  const ids = new Set(['cart-drawer']);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((c) => {
    if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

/**
 * Promote a bare-but-staged cart line to a Prosuro-priced line via /cart/change.js.
 * @param {any} line  a /cart.js line object (key, quantity, price, properties)
 * @param {{id:string,term:string,sku:string,price:string,label:string}} plan
 * @returns {Promise<boolean>}
 */
async function promotePlanOnLine(line, plan) {
  const token = (line.properties && line.properties._StagedPlanOptions) ||
    readProsuroInput('prosuroAttribute-planOptions'); // PDP fallback (B); cart (C) relies on the staged token
  if (!token) { console.warn('[sole-warranty] no Prosuro token to promote — line stays unpriced'); return false; }

  const real = resolveProsuroPlan(plan, token);
  if (!real) { console.warn('[sole-warranty] selected term not in Prosuro token — cannot price'); return false; }

  // Full merged property set — /cart/change.js REPLACES properties, so we must re-send everything.
  const props = { ...(line.properties || {}) };
  props._PlanOptions = token;
  props._SelectedPlan = `${real.id}-${real.price}`;            // term-SKU-price, real per-product tier
  props.Product = `$${(line.price / 100).toFixed(2)}`;          // shown on the line
  props.Protection = planPropValue(real);                       // shown on the line

  const changeUrl = window.Theme?.routes?.cart_change_url || '/cart/change.js';
  window.__prosuroSkipCartMonkeyPatch = true; // stop Prosuro's patch from racing our morph (its own flag)
  try {
    const res = await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: line.key,                 // old key still finds the line; the response re-keys it (don't reuse)
        quantity: line.quantity,
        properties: props,
        sections: cartSectionIds(),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) return false;
    const updated = await res.json();
    // Refresh via the theme's own morph (cart-items-component reads detail.data.sections[sectionId]).
    document.dispatchEvent(new CartUpdateEvent(updated, 'sole-warranty', {
      source: 'sole-warranty', itemCount: updated.item_count, sections: updated.sections,
    }));
    trackGA('warranty_added', plan);
    return true;
  } catch (e) {
    console.error('[sole-warranty] prosuro promote failed', e);
    return false;
  } finally {
    window.__prosuroSkipCartMonkeyPatch = false;
  }
}

/**
 * Resolve the target machine line (bare + staged + not yet protected) and promote it.
 * @param {{id:string,term:string,sku:string,price:string,label:string}} plan
 * @param {string|number} [variantId]  prefer the machine with this variant (variant C card / variant B add)
 */
async function prosuroPromote(plan, variantId) {
  try {
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    const staged = (cart.items || []).filter(
      (i) => i.properties && i.properties._StagedPlanOptions && !i.properties._SelectedPlan
    );
    let line = variantId ? staged.find((i) => String(i.variant_id) === String(variantId)) : null;
    if (!line) line = staged[staged.length - 1]; // most-recently-added staged machine
    if (!line) { console.warn('[sole-warranty] no staged machine line to protect'); return false; }
    return await promotePlanOnLine(line, plan);
  } catch (e) {
    console.error('[sole-warranty] prosuroPromote failed', e);
    return false;
  }
}

/* ----------------------------------------------------------------------- attach adapter */

/**
 * Attach a plan to the cart.
 * @param {{id:string,term:string,sku:string,price:string,label:string}} plan
 * @param {string} [parentVariantId] the machine variant the plan protects (for display/linking)
 */
function attachPlan(plan, parentVariantId) {
  if (!config || !plan) return;

  if (config.attachMode === 'prosuro') {
    // Production: hand off to the live Prosuro app (it owns pricing + Guardsman registration).
    // Re-resolve to the real per-product tier so this fallback path never dispatches the flat placeholder.
    const real = resolveProsuroPlan(plan) || plan;
    document.dispatchEvent(new CustomEvent('protectionAdded', {
      detail: { term: real.term, sku: real.sku, price: real.price },
    }));
    trackGA('warranty_added', real);
    return;
  }

  // Standalone (preview/test): add the test product as its own line, reusing sole-cart-ajax.js.
  const form = document.createElement('form');
  form.action = (window.Theme?.routes?.cart_url || '/cart') + '/add';
  form.method = 'post';
  form.hidden = true;
  form.setAttribute('data-sole-warranty-form', '');
  const add = (name, value) => {
    const i = document.createElement('input');
    i.type = 'hidden'; i.name = name; i.value = value; form.appendChild(i);
  };
  add('id', config.testVariantId);
  add('quantity', '1');
  // NOTE: standalone is the PREVIEW-ONLY test backend — the line prices at the $9.99 test product, NOT the
  // real tier. Do NOT emit a $-valued `Protection` property here: showing "$109.99" next to a $9.99 line
  // reads as a real (and contradictory) price at checkout. Label the plan as a preview test instead, so a
  // standalone build can never be mistaken for real pricing. Real tier pricing comes from `prosuro` mode.
  add('properties[Plan]', `${plan.label} — preview test ($${plan.price} applies in prosuro mode)`);
  if (config.product?.title) add('properties[For]', config.product.title);
  add('properties[_SelectedPlan]', `${plan.id}-${plan.price}`);
  add('properties[_sole_warranty]', '1');
  if (parentVariantId) add('properties[_parent_variant]', parentVariantId);

  const submit = document.createElement('button');
  submit.type = 'submit'; submit.hidden = true; form.appendChild(submit);
  document.body.appendChild(form);
  form.requestSubmit(submit); // sole-cart-ajax.js intercepts → adds → morphs drawer/cart → cart:update
  setTimeout(() => form.remove(), 0);
  trackGA('warranty_added', plan);
}

/* ----------------------------------------------------------------------- variant A: inline dropdown */

function initInlineDropdown() {
  const fieldset = document.querySelector('[data-sole-warranty-fieldset]');
  if (!fieldset) return;
  const prosuro = config?.attachMode === 'prosuro';
  fieldset.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.checked) {
      const plan = getSelectedPlan(fieldset);
      if (plan) trackGA('select_warranty_plan', plan);
      fieldset.classList.toggle('has-selection', !!plan);
      // prosuro mode: mirror the plan onto the buy-box form so it rides the machine ATC (priced by Prosuro).
      if (prosuro) prosuroSyncForm(plan);
    }
  });
  // Reflect whatever is selected at load (clears any stale prosuro props if nothing/decline is checked).
  if (prosuro) prosuroSyncForm(getSelectedPlan(fieldset));
}

/* ----------------------------------------------------------------------- variant B: post-ATC dialog */

let warrantyDialog = null;

function openWarrantyDialog() {
  warrantyDialog = warrantyDialog || document.getElementById('sole-warranty-dialog');
  if (!warrantyDialog || typeof warrantyDialog.showModal !== 'function') return false;
  // Never offer an empty choice. applyRealPricesToDom hides every term the per-product token doesn't offer, so
  // a product Prosuro hasn't enrolled (or whose token never loaded) leaves "Choose your plan" with nothing
  // under it and a dead Add button. Seen live 2026-10-05 on the new R92-2026 record before Prosuro enrolled it.
  const offered = [...warrantyDialog.querySelectorAll('input[data-plan-id]')].filter((input) =>
    input.getAttribute('data-plan-id') !== 'decline' && !input.closest('[hidden]'));
  if (!offered.length) {
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'warranty_dialog_skipped_no_plans', product_title: config?.product?.title || null }); } catch (_) {}
    return false;
  }
  trackGA('view_warranty', null);
  if (!warrantyDialog.open) warrantyDialog.showModal();
  return true;
}

function initPopupDialog() {
  warrantyDialog = document.getElementById('sole-warranty-dialog');
  if (!warrantyDialog) return;

  const fieldset = warrantyDialog.querySelector('[data-sole-warranty-fieldset]');
  const addBtn = warrantyDialog.querySelector('[data-sole-warranty-add]');
  // The v2 dialog has MORE THAN ONE decline control (the close ✕ AND the "No thanks" button) — bind ALL of
  // them, not just the first (querySelector would only wire the ✕, leaving "No thanks" dead). #bugfix
  const declineBtns = warrantyDialog.querySelectorAll('[data-sole-warranty-decline]');

  // Selecting a plan via its label/card immediately enables the primary CTA.
  fieldset?.addEventListener('change', () => {
    const plan = getSelectedPlan(fieldset);
    if (addBtn) addBtn.disabled = !plan;
  });

  addBtn?.addEventListener('click', async () => {
    const plan = getSelectedPlan(fieldset);
    warrantyDialog.close();
    if (plan) {
      // prosuro: promote the just-added (bare + staged) machine line; standalone: add a test line.
      if (config?.attachMode === 'prosuro') await prosuroPromote(plan);
      else attachPlan(plan);
    }
    afterDialog();
  });
  declineBtns.forEach((btn) => btn.addEventListener('click', () => {
    trackGA('click_warranty_decline', null);
    warrantyDialog.close();
    afterDialog();
  }));
}

/** After the popup resolves, open the cart drawer so the shopper sees the result. */
function afterDialog() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') setTimeout(() => drawer.open(), 120);
}

/* ----------------------------------------------------------------------- variant C: cart card */

function initCartCard(root = document) {
  const cards = root.querySelectorAll('[data-sole-warranty-card]');
  // The cart card belongs to the cart_only variant only. On a dropdown/popup PDP the PDP config is
  // present with a different uiMode → hide the drawer card so we don't double-offer.
  if (config && config.uiMode !== 'cart_only') {
    cards.forEach((c) => { c.hidden = true; });
    return;
  }
  cards.forEach((card) => {
    if (card.hasAttribute('data-bound')) return;
    card.setAttribute('data-bound', '');
    const fieldset = card.querySelector('[data-sole-warranty-fieldset]');
    const addBtn = card.querySelector('[data-sole-warranty-add]');
    fieldset?.addEventListener('change', () => {
      const plan = getSelectedPlan(fieldset);
      if (addBtn) addBtn.disabled = !plan;
    });
    addBtn?.addEventListener('click', async () => {
      const plan = getSelectedPlan(fieldset);
      if (!plan) return;
      const parentVariant = card.getAttribute('data-parent-variant') || undefined;
      // The card only renders for a machine that rode the staged Prosuro token (see sole-warranty-card.liquid),
      // so it always promotes that line — this also makes the card work on the /cart PAGE, where the PDP
      // config (and its attachMode) isn't present. Fall back to a standalone test line only if no staged line.
      const promoted = await prosuroPromote(plan, parentVariant);
      if (!promoted) attachPlan(plan, parentVariant);
    });
  });
}

/* --------------------------------------------------- per-line cart warranty (each machine gets its own offer) */

/** Remove protection from a line: drop the selection + keep the token staged so the per-line offer returns. */
async function removePlanOnLine(line) {
  const props = { ...(line.properties || {}) };
  const token = props._PlanOptions || props._StagedPlanOptions;
  delete props._PlanOptions;   // /cart/change.js REPLACES props → omitting a key removes it
  delete props._SelectedPlan;  // no selection → Cart Transform charges nothing
  delete props.Product;
  delete props.Protection;
  if (token) props._StagedPlanOptions = token; // keep the token so the offer re-renders on this line
  const changeUrl = window.Theme?.routes?.cart_change_url || '/cart/change.js';
  window.__prosuroSkipCartMonkeyPatch = true;
  try {
    const res = await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: props,
        sections: cartSectionIds(), sections_url: window.location.pathname }),
    });
    if (!res.ok) return false;
    const updated = await res.json();
    document.dispatchEvent(new CartUpdateEvent(updated, 'sole-warranty', {
      source: 'sole-warranty', itemCount: updated.item_count, sections: updated.sections }));
    trackGA('warranty_removed', {});
    return true;
  } catch (e) { console.error('[sole-warranty] remove failed', e); return false; }
  finally { window.__prosuroSkipCartMonkeyPatch = false; }
}

/** Bind the per-line warranty offer/remove controls in the cart. Cart-scoped — NOT gated on any PDP config. */
function initCartLineWarranty(root = document) {
  root.querySelectorAll('[data-sole-warranty-line]').forEach((box) => {
    if (box.hasAttribute('data-bound')) return;
    box.setAttribute('data-bound', '');
    const fieldset = box.querySelector('[data-sole-warranty-fieldset]');
    const addBtn = box.querySelector('[data-sole-warranty-line-add]');
    const key = box.getAttribute('data-line-key');
    if (!fieldset || !addBtn || !key) return;
    fieldset.addEventListener('change', () => { addBtn.disabled = !getSelectedPlan(fieldset); });
    addBtn.addEventListener('click', async () => {
      const plan = getSelectedPlan(fieldset);
      if (!plan) return;
      addBtn.disabled = true;
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        const line = (cart.items || []).find((i) => i.key === key); // re-fetch: line keys change on mutation
        if (line) { const ok = await promotePlanOnLine(line, plan); if (!ok) addBtn.disabled = false; }
        else addBtn.disabled = false;
      } catch (e) { console.error('[sole-warranty] per-line add failed', e); addBtn.disabled = false; }
    });
  });
  root.querySelectorAll('[data-sole-warranty-line-remove]').forEach((btn) => {
    if (btn.hasAttribute('data-bound')) return;
    btn.setAttribute('data-bound', '');
    const key = btn.getAttribute('data-line-key');
    if (!key) return;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        const line = (cart.items || []).find((i) => i.key === key);
        if (line) await removePlanOnLine(line); else btn.disabled = false;
      } catch (e) { console.error('[sole-warranty] per-line remove failed', e); btn.disabled = false; }
    });
  });
}

/* ----------------------------------------------------------------------- wiring */

// Capture-phase: flag product add-to-cart submits (we only set state here; order vs sole-cart-ajax is irrelevant).
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.hasAttribute('data-sole-warranty-form')) return; // our own warranty add
  const action = form.getAttribute('action') || '';
  if (!action.includes('/cart/add')) return;
  if (!config) return;
  // Only the main PDP buy-box form arms the warranty flow — NOT accessory upsell mini-forms (which would
  // otherwise pop the dialog / attach a plan when a shopper adds an accessory).
  if (!form.classList.contains('pdp7-form')) return;

  if (config.uiMode === 'dropdown') {
    // prosuro: the 4 properties already ride this form (prosuroSyncForm) → nothing to do post-add.
    // standalone: queue a separate test-product line to add after the machine morph settles.
    if (config.attachMode !== 'prosuro') {
      const plan = getSelectedPlan(document.querySelector('[data-sole-warranty-fieldset]'));
      if (plan) pendingWarranty = { plan, parent: form.querySelector('[name="id"]')?.value };
    }
  } else if (config.uiMode === 'popup') {
    awaitingMachineForPopup = true;
  }
}, true);

// After a machine add resolves (sole-cart-ajax dispatches cart:update), run the variant's follow-up.
document.addEventListener('cart:update', () => {
  if (pendingWarranty) {
    const { plan, parent } = pendingWarranty;
    pendingWarranty = null;
    setTimeout(() => attachPlan(plan, parent), 150); // let the machine morph settle first
    return;
  }
  if (awaitingMachineForPopup && config?.uiMode === 'popup') {
    awaitingMachineForPopup = false;
    openWarrantyDialog();
  }
  // Cart re-rendered → (re)bind any cart card that appeared in the morph. Do this UNCONDITIONALLY (not gated
  // on config.uiMode): on a non-warranty page `config` is null at load, so a card that morphs in after an add
  // would otherwise never get its change-listener bound and its Add button would stay disabled.
  initCartCard();
  initCartLineWarranty();
  applyTermsLinks();
});

function init() {
  if (config?.uiMode === 'popup' || config?.uiMode === 'cart_only') {
    // These variants must not let the drawer pop on the machine add (popup/card takes over).
    document.querySelectorAll('cart-drawer-component[auto-open]').forEach((d) => {
      if (config.uiMode === 'popup') d.removeAttribute('auto-open');
    });
  }
  // prosuro B/C: stage the per-product token onto the buy-box form so the bare machine add carries it
  // (under `_StagedPlanOptions`, not `_PlanOptions`, so Prosuro's auto-offer stays dormant). Promotion to
  // a priced line happens on accept (popup/card) via /cart/change.js.
  if (config?.attachMode === 'prosuro' && (config.uiMode === 'popup' || config.uiMode === 'cart_only')
      && document.querySelector('form.pdp7-form')) {
    prosuroStageToken();
  }
  if (config?.uiMode === 'dropdown') initInlineDropdown();
  if (config?.uiMode === 'popup') initPopupDialog();
  // prosuro PDP surfaces: rewrite displayed prices from the per-product token once it loads, so the UI shows
  // the REAL per-product price (not the flat placeholder) and hides terms/products Prosuro doesn't cover.
  if (config?.attachMode === 'prosuro' && document.querySelector('form.pdp7-form')
      && (config.uiMode === 'dropdown' || config.uiMode === 'popup')) {
    whenProsuroToken((tok) => { applyRealPricesToDom(); applyTermsLinks(tok); });
  }
  // cart_only cards exist regardless of a PDP config (they live in the cart) — always try to bind.
  initCartCard();
  initCartLineWarranty();
  applyTermsLinks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
